import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import "./PaymentProcess.scss";

export default function PaymentProcess() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [payment, setPayment] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    fetchAuctionAndInvoice();
  }, [auctionId]);

  const fetchAuctionAndInvoice = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
      }

      // Fetch invoice (includes payment_deadline from backend - 72h excluding holidays from invoice creation)
      try {
        const invoiceResponse = await CommonService.get(`${API_URLS.AUCTION.PROFORMA_INVOICE(auctionId)}`);
        if (invoiceResponse && invoiceResponse.success && invoiceResponse.data) {
          setInvoice(invoiceResponse.data);
        }
      } catch (error) {
        console.error("Error fetching invoice:", error);
      }

      // Fetch payment if exists (may 404 if buyer hasn't initiated payment yet)
      try {
        const paymentResponse = await CommonService.get(API_URLS.AUCTION.GET_PAYMENT_BY_AUCTION(auctionId));
        if (paymentResponse && paymentResponse.success && paymentResponse.data) {
          setPayment(paymentResponse.data);
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          console.error("Error fetching payment:", error);
        }
        setPayment(null);
      }

      // Fetch seller company profile for bank details
      // The auction query joins with company_profile, so we can try to get company_id from there
      // or directly query using user_login_id
      if (auctionResponse.data.user_login_id) {
        try {
          // The auction response might have seller_company from the JOIN, but we need company_id
          // Try to fetch company profile - the backend might accept user_login_id or we need company_id
          // First, try using user_login_id directly (some backends accept this)
          let sellerProfileData = null;
          
          try {
            // Try with user_login_id first
            const profileResponse = await CommonService.get(`/company/profile/${auctionResponse.data.user_login_id}`);
            if (profileResponse && profileResponse.success && profileResponse.data) {
              sellerProfileData = profileResponse.data;
            }
          } catch (err) {
            // If that fails, the endpoint likely requires company_id
            // We need to query the company_profile table to get company_id from user_login_id
            // Since we can't do that directly, we'll show a message that bank details need to be added
            console.warn("Could not fetch seller profile using user_login_id. The seller needs to ensure their bank details are added to their company profile.");
          }
          
          if (sellerProfileData) {
            console.log("Seller profile fetched:", sellerProfileData);
            console.log("Bank details:", sellerProfileData.bank_details);
            setSellerProfile(sellerProfileData);
          }
        } catch (error) {
          console.error("Error fetching seller profile:", error);
        }
      }
    } catch (error) {
      console.error("Error fetching auction and invoice:", error);
      toast.error("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) {
      return "₹0";
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy");
    });
  };

  const handleReopenBid = async () => {
    if (!window.confirm("Reopen the bid? The winning bid will be released and you can declare a new winner from Review Bids.")) return;
    try {
      setReopening(true);
      const response = await CommonService.post(API_URLS.AUCTION.REOPEN_BID(auctionId), {});
      if (response && response.success) {
        toast.success("Bid reopened successfully. You can declare a new winner from Review Bids.");
        navigate(`/auction/${auctionId}/review-bids`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error?.response?.data?.error || "Failed to reopen bid";
      toast.error(msg);
    } finally {
      setReopening(false);
    }
  };

  const handleCompletePayment = async () => {
    if (!selectedMethod) {
      toast.error("Please select a payment method");
      return;
    }

    if (!agreed) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    if (!auction || !invoice || !auction.winning_bid_id) {
      toast.error("Missing auction or bid information");
      return;
    }

    // Get seller bank details (calculate inside handler to ensure it's available)
    let bankDetailsData = {};
    if (sellerProfile?.bank_details) {
      if (typeof sellerProfile.bank_details === 'string') {
        try {
          bankDetailsData = JSON.parse(sellerProfile.bank_details);
        } catch (e) {
          console.error("Error parsing bank_details:", e);
          bankDetailsData = {};
        }
      } else {
        bankDetailsData = sellerProfile.bank_details;
      }
    }

    // Validate bank details for Direct Bank Transfer
    if (selectedMethod === 'Direct Bank Transfer') {
      if (!sellerProfile?.bank_details || 
          !bankDetailsData.account_number || 
          bankDetailsData.account_number === 'N/A' ||
          !bankDetailsData.ifsc_code || 
          bankDetailsData.ifsc_code === 'N/A') {
        toast.error("Seller bank details are not available. Please contact the seller or use Escrow Service.");
        return;
      }
    }

    try {
      // Prepare payment data
      const winningBidAmount = parseFloat(invoice.subtotal) || 0;
      const gstAmount = parseFloat(invoice.gst_amount) || 0;
      
      if (winningBidAmount <= 0) {
        toast.error("Invalid payment amount");
        return;
      }
      
      const paymentData = {
        auction_id: parseInt(auctionId),
        winning_bid_id: auction.winning_bid_id,
        payment_method: selectedMethod,
        winning_bid_amount: winningBidAmount,
        gst_amount: gstAmount,
        // Seller bank details for Direct Bank Transfer
        ...(selectedMethod === 'Direct Bank Transfer' && {
          seller_bank_account_name: bankDetailsData.account_holder_name || sellerProfile?.company_name || null,
          seller_bank_account_number: bankDetailsData.account_number || null,
          seller_bank_ifsc: bankDetailsData.ifsc_code || null,
          seller_bank_name: bankDetailsData.bank_name || null,
          seller_bank_branch: bankDetailsData.branch_name || null,
        })
      };

      console.log("Creating payment with data:", paymentData);

      // Create payment record (INSERT into database)
      const response = await CommonService.post(API_URLS.AUCTION.PAYMENTS, paymentData);
      
      if (response && response.success) {
        console.log("Payment created successfully. Payment ID:", response.data?.paymentId);
        
        // Navigate to payment gateway page
        navigate(`/auction/${auctionId}/payment/gateway`, {
          state: {
            paymentData: paymentData,
            paymentId: response.data?.paymentId,
            paymentMethod: selectedMethod
          }
        });
      } else {
        console.error("Payment creation failed:", response);
        toast.error(response?.message || "Failed to process payment");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Failed to process payment";
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="payment-process-page">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  if (!auction || !invoice) {
    return (
      <div className="payment-process-page">
        <div className="error-container">Auction or invoice not found</div>
      </div>
    );
  }

  // Calculate escrow fee (1% of subtotal)
  const winningBidAmount = parseFloat(invoice.subtotal) || 0;
  const escrowFee = selectedMethod === 'Escrow Service' ? winningBidAmount * 0.01 : 0;
  const gstAmount = parseFloat(invoice.gst_amount) || 0;
  const totalAmount = winningBidAmount + gstAmount;
  const totalPayable = totalAmount + escrowFee;

  // Get seller bank details
  // Handle both parsed object and string (in case backend doesn't parse it)
  let bankDetails = {};
  if (sellerProfile?.bank_details) {
    if (typeof sellerProfile.bank_details === 'string') {
      try {
        bankDetails = JSON.parse(sellerProfile.bank_details);
      } catch (e) {
        console.error("Error parsing bank_details:", e);
        bankDetails = {};
      }
    } else {
      bankDetails = sellerProfile.bank_details;
    }
  }
  
  // Debug logging
  if (!sellerProfile) {
    console.warn("Seller profile not loaded");
  } else if (!bankDetails || Object.keys(bankDetails).length === 0) {
    console.warn("Bank details not found in seller profile:", sellerProfile);
  }
  
  const sellerAccountName = bankDetails?.account_holder_name || sellerProfile?.company_name || auction.seller_company || auction.seller_name || "N/A";
  const sellerAccountNumber = bankDetails?.account_number || "N/A";
  const sellerIFSC = bankDetails?.ifsc_code || "N/A";
  const sellerBankName = bankDetails?.bank_name || "N/A";
  const sellerBranch = bankDetails?.branch_name || "N/A";

  // Get GST rate
  const gstRate = auction.gst_rate || 18;
  const gstPercentage = `${gstRate}%`;

  // Format seller location
  const formatSellerLocation = () => {
    if (sellerProfile?.address) {
      try {
        const address = typeof sellerProfile.address === 'string' 
          ? JSON.parse(sellerProfile.address) 
          : sellerProfile.address;
        if (address.city) return address.city;
        if (address.state) return address.state;
        if (address.country) return address.country;
      } catch (e) {
        // Ignore parse errors
      }
    }
    return auction.city || auction.country || "India";
  };

  const user = StorageService.getUser();
  const isSeller = user && auction && Number(user.user_login_id) === Number(auction.user_login_id);
  const paymentDeadline = invoice?.payment_deadline || payment?.sla_deadline;
  const isPaymentDeadlinePassed = paymentDeadline && new Date(paymentDeadline) <= new Date();
  const isPaymentCompleted = payment?.payment_status === "completed";
  const formatDeadline = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  return (
    <div className="payment-process-page">
      <div className="payment-container">
        <button className="back-btn" onClick={handleBack}>
          ← Back
        </button>

        <h1 className="page-title">Complete Payment</h1>

        {isPaymentDeadlinePassed && !isPaymentCompleted && (
          <div className="payment-deadline-expired">
            <p className="payment-deadline-message">
              <strong>Payment deadline has passed.</strong> The buyer did not complete payment within 72 hours (excluding holidays). You can reopen the bid to declare a new winner from Review Bids.
            </p>
            {isSeller && (
              <button type="button" className="btn-reopen-bid" onClick={handleReopenBid} disabled={reopening}>
                {reopening ? "Reopening…" : "Reopen bid"}
              </button>
            )}
            {!isSeller && (
              <p className="payment-deadline-note">The seller can reopen the bid. Please contact the seller or wait for further updates.</p>
            )}
          </div>
        )}

        {!isPaymentDeadlinePassed && paymentDeadline && (
          <p className="payment-deadline-info">
            Complete payment within <strong>72 hours (excluding holidays)</strong>. Deadline: {formatDeadline(paymentDeadline)}
          </p>
        )}

        {!(isPaymentDeadlinePassed && !isPaymentCompleted) && (
        <>
        <div className="payment-layout">
          <div className="main-column">
          {/* Order Summary Card */}
          <div className="order-summary-card">
            <div className="card-header">
              <h2 className="card-title">Order Summary</h2>
            </div>
            <div className="card-body">
              {/* Product Card */}
              <div className="product-card">
                <div className="product-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                  </svg>
                </div>
                <div className="product-info">
                  <h3 className="product-name">{auction.product_name}</h3>
                  <p className="product-description">
                    {auction.product_description || "No description available"}
                  </p>
                  <div className="product-attributes">
                    <span className="attribute">Category: {auction.category_name || "N/A"}</span>
                    <span className="attribute">Quantity: {invoice.quantity || auction.quantity || 1} Unit{invoice.quantity > 1 || auction.quantity > 1 ? 's' : ''}</span>
                  </div>
                </div>
              </div>

              <div className="summary-divider"></div>

              {/* Financial Breakdown */}
              <div className="summary-item">
                <span className="summary-label">Winning Bid Amount:</span>
                <span className="summary-value">{formatCurrency(winningBidAmount)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">GST ({gstPercentage}):</span>
                <span className="summary-value">{formatCurrency(gstAmount)}</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-item total-item">
                <span className="summary-label">Total Amount:</span>
                <span className="summary-value">{formatCurrency(totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection Card */}
          <div className="payment-method-card">
            <div className="card-header">
              <h2 className="card-title">Select Payment Method</h2>
            </div>
            <div className="card-body">
              {/* Direct Bank Transfer Option */}
              <div 
                className={`payment-option ${selectedMethod === 'Direct Bank Transfer' ? 'selected' : ''}`}
                onClick={() => setSelectedMethod('Direct Bank Transfer')}
              >
                <div className="option-header">
                  <div className="option-icon bank-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="1" x2="12" y2="23"></line>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    </svg>
                  </div>
                  <div className="option-content">
                    <h3 className="option-title">Direct Bank Transfer</h3>
                    <p className="option-description">Transfer funds directly to seller's bank account</p>
                    <div className="option-badges">
                      <span className="badge badge-instant">Instant</span>
                      <span className="badge badge-no-fee">No Extra Fees</span>
                    </div>
                  </div>
                  <div className="radio-button">
                    {selectedMethod === 'Direct Bank Transfer' && <div className="radio-fill"></div>}
                  </div>
                </div>
                {selectedMethod === 'Direct Bank Transfer' && (
                  <div className="option-details">
                    <div className="bank-details">
                      <div className="bank-header">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="1" x2="12" y2="23"></line>
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                        </svg>
                        <h4>Seller's Bank Details</h4>
                      </div>
                      <div className="bank-info">
                        <div className="bank-row">
                          <span className="bank-label">Account Name:</span>
                          <span className="bank-value">{sellerAccountName}</span>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">Account Number:</span>
                          <div className="bank-value-with-copy">
                            <span className="bank-value">{sellerAccountNumber}</span>
                            <button 
                              className="copy-btn" 
                              onClick={() => handleCopyToClipboard(sellerAccountNumber)}
                              title="Copy to clipboard"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">IFSC Code:</span>
                          <div className="bank-value-with-copy">
                            <span className="bank-value">{sellerIFSC}</span>
                            <button 
                              className="copy-btn" 
                              onClick={() => handleCopyToClipboard(sellerIFSC)}
                              title="Copy to clipboard"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">Bank Name:</span>
                          <span className="bank-value">{sellerBankName}</span>
                        </div>
                        <div className="bank-row">
                          <span className="bank-label">Branch:</span>
                          <span className="bank-value">{sellerBranch}</span>
                        </div>
                      </div>

                      {/* Important Instructions */}
                      <div className="important-instructions">
                        <div className="instructions-icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                            <line x1="12" y1="9" x2="12" y2="13"></line>
                            <line x1="12" y1="17" x2="12.01" y2="17"></line>
                          </svg>
                        </div>
                        <div className="instructions-content">
                          <strong>Important Instructions:</strong>
                          <ul>
                            <li>Transfer exactly {formatCurrency(totalAmount)}</li>
                            <li>Add reference: {invoice.invoice_number || `AUCTION-${auctionId}`}</li>
                            <li>Keep payment receipt/screenshot</li>
                            <li>Upload proof after transfer</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Escrow Service Option */}
              <div 
                className={`payment-option ${selectedMethod === 'Escrow Service' ? 'selected' : ''}`}
                onClick={() => setSelectedMethod('Escrow Service')}
              >
                <div className="option-header">
                  <div className="option-icon escrow-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </div>
                  <div className="option-content">
                    <h3 className="option-title">Escrow Service</h3>
                    <p className="option-description">Secure payment protection - funds released after confirmation</p>
                    <div className="option-badges">
                      <span className="badge badge-protected">Protected</span>
                      <span className="badge badge-fee">1% Fee</span>
                      <span className="badge badge-recommended">Recommended</span>
                    </div>
                  </div>
                  <div className="radio-button">
                    {selectedMethod === 'Escrow Service' && <div className="radio-fill"></div>}
                  </div>
                </div>
                {selectedMethod === 'Escrow Service' && (
                  <div className="option-details">
                    <div className="escrow-protection">
                      <h4>Escrow Payment Protection</h4>
                      <p className="escrow-subtitle">How Escrow Works:</p>
                      <div className="escrow-steps">
                        <div className="escrow-step">
                          <div className="step-number">1</div>
                          <div className="step-text">Your payment is held securely by Trocanet escrow service</div>
                        </div>
                        <div className="escrow-step">
                          <div className="step-number">2</div>
                          <div className="step-text">Seller is notified of payment and proceeds with delivery</div>
                        </div>
                        <div className="escrow-step">
                          <div className="step-number">3</div>
                          <div className="step-text">You confirm receipt and satisfaction with the product</div>
                        </div>
                        <div className="escrow-step">
                          <div className="step-number">4</div>
                          <div className="step-text">Funds are released to seller after your confirmation</div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Breakdown for Escrow */}
                    <div className="payment-breakdown">
                      <div className="breakdown-item">
                        <span className="breakdown-label">Total Amount:</span>
                        <span className="breakdown-value">{formatCurrency(totalAmount)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span className="breakdown-label">Escrow Fee (1%):</span>
                        <span className="breakdown-value">{formatCurrency(escrowFee)}</span>
                      </div>
                      <div className="breakdown-divider"></div>
                      <div className="breakdown-item total-breakdown">
                        <span className="breakdown-label">Total Payable:</span>
                        <span className="breakdown-value">{formatCurrency(totalPayable)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Protection Notice */}
          {selectedMethod && (
            <div className="payment-protection-notice">
              <div className="protection-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
              </div>
              <div className="protection-text">
                Your payment is 100% protected. Funds will only be released after you confirm receipt and satisfaction with the purchased item.
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Seller Info, Security, Help */}
        <div className="right-column">
          {/* Seller Information */}
          <div className="info-card seller-info-card">
            <h3 className="info-card-title">Seller Information</h3>
            <div className="info-card-body">
              <div className="info-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Seller {sellerProfile?.company_name || auction.seller_company || auction.seller_name || "N/A"} - {formatSellerLocation()}</span>
              </div>
              <div className="info-item">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>Location {formatSellerLocation()}</span>
              </div>
            </div>
          </div>

          {/* Payment Security */}
          <div className="info-card security-card">
            <div className="security-header">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              <h3 className="info-card-title">Payment Security</h3>
            </div>
            <div className="info-card-body">
              <ul className="security-features">
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Secure encrypted transactions
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  PCI DSS compliant
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Buyer protection guarantee
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  24/7 fraud monitoring
                </li>
              </ul>
            </div>
          </div>

          {/* Need Help */}
          <div className="info-card help-card">
            <h3 className="info-card-title">Need Help?</h3>
            <div className="info-card-body">
              <p className="help-description">
                Our support team is available to assist you with the payment process.
              </p>
              <div className="help-contact">
                <div className="contact-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>+91 1800 123 4567</span>
                </div>
                <div className="contact-item">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>support@trocanet.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        {/* Terms and Conditions */}
        <div className="terms-section">
          <label className="terms-checkbox">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <span>
              I agree to the terms and conditions. I understand that this payment is for the purchase of {auction.product_name} at {formatCurrency(winningBidAmount)} and this transaction is binding upon completion.
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="btn-cancel" onClick={handleBack}>
            Cancel
          </button>
          <button 
            className="btn-complete-payment" 
            onClick={handleCompletePayment}
            disabled={!selectedMethod || !agreed}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            Complete Secure Payment
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
